import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';

const SortableSection = ({ 
  section, 
  index, 
  onSectionChange, 
  onRemoveSection, 
  canRemove, 
  errors,
  forms = [],
  t: passedT,
  onManageEnrollment,
  showEnrollmentButton = false
}) => {
  const { t: defaultT } = useTranslation();
  const t = passedT || defaultT;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: section.id || `section-${index}` 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50 ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-move p-1 text-gray-500 hover:text-gray-700"
            title={t('events.modal.dragHandle')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M7 2a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 2zM7 8a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 8zM7 14a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 14zM13 2a2 2 0 1 1 .001 4.001A2 2 0 0 1 13 2zM13 8a2 2 0 1 1 .001 4.001A2 2 0 0 1 13 8zM13 14a2 2 0 1 1 .001 4.001A2 2 0 0 1 13 14z" />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">
            Section {index + 1}
            {section.section_name && ` - ${section.section_name}`}
          </h4>
        </div>
        
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemoveSection(index)}
            className="text-red-600 hover:text-red-800 p-1"
            title={t('events.modal.removeSection')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
        {showEnrollmentButton && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onManageEnrollment?.(section);
            }}
            className="ml-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1 rounded"
          >
            參加者名單
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('events.modal.sectionName')}
          </label>
          <input
            type="text"
            value={section.section_name || ''}
            onChange={(e) => onSectionChange(index, 'section_name', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder={t('events.modal.sectionNamePlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('events.modal.lecturer')}
          </label>
          <input
            type="text"
            value={section.lecturer || ''}
            onChange={(e) => onSectionChange(index, 'lecturer', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder={t('events.modal.lecturerPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('events.modal.quantity')}
          </label>
          <input
            type="number"
            min="0"
            value={section.quantity || 0}
            onChange={(e) => onSectionChange(index, 'quantity', parseInt(e.target.value) || 0)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder={t('events.modal.quantityPlaceholder')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Attach Form</label>
          <div className="flex items-center gap-2">
            <select
              value={section.form_id || ''}
              onChange={(e) => onSectionChange(index, 'form_id', e.target.value ? parseInt(e.target.value, 10) : null)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">-- None --</option>
              {forms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {section.form_id && (
              <div className="flex gap-1 mt-1">
                <a className="text-xs px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded" href={`/forms/${section.form_id}/edit`} target="_blank" rel="noreferrer">Edit</a>
                <a className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded" href={`/forms/${section.form_id}/submissions`} target="_blank" rel="noreferrer">Results</a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('events.modal.startTime')}
          </label>
          <DatePicker
            selected={section.start_at ? new Date(section.start_at) : null}
            onChange={(date) => onSectionChange(index, 'start_at', date?.toISOString())}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="yyyy-MM-dd HH:mm"
            placeholderText={t('events.modal.startTimePlaceholder')}
            className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
              errors[`sections.${index}.start_at`] ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors[`sections.${index}.start_at`] && (
            <p className="mt-1 text-sm text-red-600">{errors[`sections.${index}.start_at`]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('events.modal.endTime')}
          </label>
          <DatePicker
            selected={section.end_at ? new Date(section.end_at) : null}
            onChange={(date) => onSectionChange(index, 'end_at', date?.toISOString())}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="yyyy-MM-dd HH:mm"
            placeholderText={t('events.modal.endTimePlaceholder')}
            className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
              errors[`sections.${index}.end_at`] ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors[`sections.${index}.end_at`] && (
            <p className="mt-1 text-sm text-red-600">{errors[`sections.${index}.end_at`]}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SortableSection;
